import type { CameraMonitorItem, CameraMonitorResponse, CameraMonitorState, CollectorSummary } from "@contracts/admin";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Filter, RefreshCw, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "@frontend/api/client";
import { CameraFramePreview, CameraPreviewModal } from "@frontend/components/CameraFramePreview";
import { Button, EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@frontend/components/ui";
import { CAMERA_MONITOR_STATES, cameraMonitorStatePresentation, currentCameraMonitorPollingInterval } from "@frontend/lib/camera-monitor";
import { formatDate, formatRelative } from "@frontend/lib/format";

const PAGE_SIZE = 24;

export function CamerasPage() {
  const [collectorId, setCollectorId] = useState("");
  const [state, setState] = useState<CameraMonitorState | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<CameraMonitorItem | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const collectors = useQuery({
    queryKey: ["collectors", "camera-monitor-filter"],
    queryFn: () => apiRequest<{ data: CollectorSummary[] }>("/api/v1/admin/collectors?limit=200"),
  });
  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (collectorId) params.set("collector_id", collectorId);
    if (state) params.set("state", state);
    if (search) params.set("q", search);
    return params.toString();
  }, [collectorId, page, search, state]);
  const cameras = useQuery({
    queryKey: ["camera-monitor", page, collectorId, state, search],
    queryFn: () => apiRequest<CameraMonitorResponse>(`/api/v1/admin/cameras?${queryString}`),
    placeholderData: keepPreviousData,
    refetchInterval: currentCameraMonitorPollingInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const hasFilters = Boolean(collectorId || state || searchInput);
  const rows = cameras.data?.data ?? [];
  const pagination = cameras.data?.pagination;

  function clearFilters() {
    setCollectorId("");
    setState("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  }

  return (
    <>
      <PageHeader
        title="Câmeras"
        actions={(
          <>
            <span className="camera-monitor-updated">Atualizado {formatRelative(cameras.data?.generated_at)}</span>
            <Button aria-label="Atualizar câmeras" disabled={cameras.isFetching} variant="secondary" onClick={() => void cameras.refetch()}>
              <RefreshCw className={cameras.isFetching ? "spin" : ""} size={17} /> Atualizar
            </Button>
          </>
        )}
      />

      <section aria-label="Filtros de câmeras" className="camera-monitor-filters">
        <span className="filter-panel__title"><Filter size={17} /> Filtros</span>
        <label className="filter-search"><span>Buscar</span><span className="search-input"><Search size={16} /><input onChange={(event) => setSearchInput(event.target.value)} placeholder="Câmera ou coletor" value={searchInput} /></span></label>
        <label><span>Coletor</span><select onChange={(event) => { setCollectorId(event.target.value); setPage(1); }} value={collectorId}><option value="">Todos</option>{collectors.data?.data.map((collector) => <option key={collector.id} value={collector.id}>{collector.name}</option>)}</select></label>
        <label><span>Estado</span><select onChange={(event) => { setState(event.target.value as CameraMonitorState | ""); setPage(1); }} value={state}><option value="">Todos</option>{CAMERA_MONITOR_STATES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
      </section>

      <div className="camera-monitor-result">
        <span aria-live="polite">{pagination?.total ?? 0} {(pagination?.total ?? 0) === 1 ? "câmera" : "câmeras"}</span>
        {hasFilters ? <Button variant="ghost" onClick={clearFilters}><X size={16} /> Limpar filtros</Button> : null}
      </div>

      {cameras.isLoading ? <LoadingState label="Carregando câmeras" /> : null}
      {cameras.isError ? <ErrorState message="Falha ao carregar câmeras" retry={() => void cameras.refetch()} /> : null}
      {!cameras.isLoading && !cameras.isError && rows.length === 0 ? <EmptyState title={hasFilters ? "Nenhuma câmera neste filtro" : "Nenhuma câmera"} action={hasFilters ? <Button variant="secondary" onClick={clearFilters}>Limpar filtros</Button> : undefined} /> : null}

      {rows.length > 0 ? (
        <div className="camera-monitor-grid">
          {rows.map((camera) => <CameraMonitorCard camera={camera} key={camera.id} onOpen={setPreview} />)}
        </div>
      ) : null}

      {pagination && pagination.pages > 1 ? (
        <nav aria-label="Paginação de câmeras" className="pagination">
          <Button disabled={page <= 1} variant="secondary" onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</Button>
          <span>Página {page} de {pagination.pages}</span>
          <Button disabled={page >= pagination.pages} variant="secondary" onClick={() => setPage((value) => value + 1)}>Próxima</Button>
        </nav>
      ) : null}

      <CameraPreviewModal camera={preview} onClose={() => setPreview(null)} />
    </>
  );
}

export function CameraMonitorCard({ camera, onOpen }: { camera: CameraMonitorItem; onOpen: (camera: CameraMonitorItem) => void }) {
  const state = cameraMonitorStatePresentation(camera.monitor_state);
  return (
    <article className="camera-monitor-card">
      <CameraFramePreview camera={camera} onOpen={onOpen} />
      <div className="camera-monitor-card__body">
        <header><div><h2>{camera.display_name}</h2><span>{camera.collector.name}</span></div><StatusBadge tone={state.tone}>{state.label}</StatusBadge></header>
        <dl>
          <div><dt>Último frame</dt><dd title={formatDate(camera.latest_frame?.captured_at_ms)}>{formatRelative(camera.latest_frame?.captured_at_ms)}</dd></div>
          <div><dt>Intervalo</dt><dd>{camera.capture_every_seconds}s</dd></div>
        </dl>
      </div>
    </article>
  );
}
